/** What a doc's or a routine's row under a turn's reply says (Paper "Chat turn · one block", A1). */
import { ApiError } from "../../app/api";
import type { DocItem } from "../docs/api";
import { fileLine, plain } from "../docs/doc-view";
import type { ThreadRoutine } from "../routines/api";
import { shortNext, whenLabel } from "../routines/routine-view";

export type DocRowView = {
  title: string;
  /** "Doc · its first words", "Doc · Archived", or "Deleted". */
  detail: string;
  /** Deleted: struck through, linking nowhere. */
  gone: boolean;
  file: DocItem["file"] | undefined;
  /**
   * S-525: the row's 56×40 tile in place of its icon: an image's own
   * thumbnail, a kept PDF's page; null for the icon.
   */
  thumb: { kind: "image"; fileId: string } | { kind: "page" } | null;
};

/** What `browser_save` kept (S-525), as the doc card's `data.capture` says. */
export type Capture = {
  as: "screenshot" | "pdf";
  site: string | null;
  pages: number | null;
  /** The file it kept: a doc whose file was since replaced isn't that page any more. */
  file_id: string | null;
};

/** "Screenshot · schwinnfitness.com", "PDF · 2 pages · bikebarn.ca". */
function captureLine(c: Capture): string {
  const what = c.as === "pdf" ? "PDF" : "Screenshot";
  const pages = c.as === "pdf" && c.pages ? `${c.pages} ${c.pages === 1 ? "page" : "pages"}` : null;
  return [what, pages, c.site].filter(Boolean).join(" · ");
}

function thumbOf(file: DocItem["file"] | undefined, capture: Capture | null): DocRowView["thumb"] {
  if (file?.mime.startsWith("image/")) return { kind: "image", fileId: file.id };
  return capture?.as === "pdf" ? { kind: "page" } : null;
}

/** The row before its doc loads, or once it's gone: the card's own words. */
function unloadedRow(title: string, error: unknown, capture: Capture | null): DocRowView {
  const gone = error instanceof ApiError && error.status === 404;
  if (gone) return { title, detail: "Deleted", gone, file: undefined, thumb: null };
  const detail = capture ? captureLine(capture) : "Doc";
  return { title, detail, gone, file: undefined, thumb: thumbOf(undefined, capture) };
}

/** The card's capture while the doc still has the file it kept; a Replace ends it (Devin on #540). */
const stillKept = (capture: Capture | null, doc: DocItem) =>
  capture && (!capture.file_id || capture.file_id === doc.file?.id) ? capture : null;

/** The doc as the thread or a read by id has it; its message's title until either loads. */
export function docRowView(
  title: string,
  doc: DocItem | undefined,
  error: unknown,
  capture: Capture | null = null,
): DocRowView {
  if (!doc) return unloadedRow(title, error, capture);
  const kept = stillKept(capture, doc);
  const thumb = thumbOf(doc.file, kept);
  if (kept && !doc.archived_at) {
    return { title: doc.title, detail: captureLine(kept), gone: false, file: doc.file, thumb };
  }
  const after = doc.archived_at ? "Archived" : (fileLine(doc) ?? plain(doc.snippet, doc.title));
  return {
    title: doc.title,
    detail: after ? `Doc · ${after}` : "Doc",
    gone: false,
    file: doc.file,
    thumb,
  };
}

/** What a routine's row under a turn's reply says (S-525, Paper "Chat cards · spike (S-525)", C3). */
export type RoutineRowView = {
  title: string;
  /** "Routine · Sundays 9:00 am", "Reminder · Thu 6 Nov, 8:00 am", "Changed · Sundays 10:00 am", "Deleted". */
  detail: string;
  /** When it runs next, or why not: "Next: Sun 2 Nov", "In 2 hours", "Off", "Ended". */
  note: string | null;
  /** The note is a run to come: moss; else muted. */
  upcoming: boolean;
  /** Deleted: struck through, linking nowhere. */
  gone: boolean;
};

/**
 * The routine as the thread has it; `routines` null on an older Worker, which
 * sends none: then the line's own words, as its line said them.
 */
export function routineRowView(
  item: { changed: boolean; line: string },
  routine: ThreadRoutine | undefined,
  known: boolean,
  tz: string,
  now: Date,
): RoutineRowView {
  if (!routine) {
    return known
      ? { title: item.line, detail: "Deleted", note: null, upcoming: false, gone: true }
      : { title: item.line, detail: "Routine", note: null, upcoming: false, gone: false };
  }
  const what = item.changed ? "Changed" : routine.kind === "once" ? "Reminder" : "Routine";
  const note = shortNext(routine, tz, now);
  return {
    title: routine.title,
    detail: `${what} · ${whenLabel(routine, tz, now)}`,
    note,
    upcoming: note.startsWith("Next") || note.startsWith("In "),
    gone: false,
  };
}
